import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { PageQuery } from "../../utils/models/pageQuery";
import { PagedResponse } from "../models/responses/pagedResponse";
import { PlaceOrderQueueItem } from "../../core/models/entities/PlaceOrderQueueItem";
import { mapToPlaceOrderQueueItem } from "../models/mappers/mapPlaceOrderQueueItem";

export interface PlaceOrderQueueRepository {
  useQueueItems(
    query: PageQuery,
    options?: { refetchInterval?: number },
  ): Resource<PagedResponse<PlaceOrderQueueItem>>;
  useQueueItem(id: number): Resource<PlaceOrderQueueItem>;
  retry(id: number): Promise<PlaceOrderQueueItem>;
}

const QUEUE_ROOT_KEY = "placeOrderQueue";

export function usePlaceOrderQueueRepository(): PlaceOrderQueueRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useQueueItems(
    query: PageQuery,
    options?: { refetchInterval?: number },
  ): Resource<PagedResponse<PlaceOrderQueueItem>> {
    return useQuery(
      [QUEUE_ROOT_KEY, "list", query],
      async (): Promise<PagedResponse<PlaceOrderQueueItem>> => {
        const dto = await apiClient.getPlaceOrderQueueItems(query);
        return {
          ...dto,
          results: dto.results?.map(mapToPlaceOrderQueueItem) ?? [],
        };
      },
      {
        refetchInterval: options?.refetchInterval,
        // The queue surface is monitoring; we want fresh data on every nav back.
        staleTime: 0,
      },
    );
  }

  function useQueueItem(id: number): Resource<PlaceOrderQueueItem> {
    return useQuery(
      [QUEUE_ROOT_KEY, "item", id],
      async () =>
        mapToPlaceOrderQueueItem(await apiClient.getPlaceOrderQueueItem(id)),
      {
        enabled: id > 0,
        staleTime: 0,
      },
    );
  }

  async function retry(id: number): Promise<PlaceOrderQueueItem> {
    const result = mapToPlaceOrderQueueItem(
      await apiClient.retryPlaceOrderQueueItem(id),
    );
    await queryClient.invalidateQueries({ queryKey: [QUEUE_ROOT_KEY] });
    return result;
  }

  return {
    useQueueItems,
    useQueueItem,
    retry,
  };
}
